import { z } from "zod";
//#region src/nats-grant-generation.ts
/**
* The wire contract of the internal route that carries a grant write to the live credential
* (2026-10-05-stream-resource-permissions, overview D-7).
*
* No channel ran from core-api to the operator program, so a new `nats_stream` grant row reached no
* running pod. The operator program now reads one operator-only route of core-api: `GET`
* {@link NATS_GRANT_GENERATION_PATH}, one DP-wide generation of the grant rows of the four NATS grant
* types (`NATS_GRANT_RESOURCE_TYPES` in `stream-verbs.ts`, overview D-10). The operator program reads
* it every poll cycle (`POLL_INTERVAL_SECONDS`) and requests a full reconcile when it changes. The
* first read sets the baseline.
*
* Producer: core-api's `NatsProjectionController` (`internal/nats-projection`) under
* `OperatorTokenGuard`, which checks its answer against the schema here. Consumer: the operator
* program's `CoreApiInstanceClient`, which parses every answer with it. The schema carries no
* `.transform()`, so `createOpenApiDocument()` renders it.
*
* The answer carries no grant row, no subject and no principal attribute: a generation is 64
* lower-case hex characters, and equality is the one operation a reader may apply to it.
*
* The 40.0.0 major (2026-10-14-app-pools-runtime-deletion, R2, R-081, R-083) removed the digest
* request and answer of the retired `POST` grant-digests route, with the principal cap that bounded
* the request. Its compute-unit reader left in stage S5, and core-api deleted the route in stage S6
* (R-056 item 2).
*/
/** The generation route: `GET`, operator-only, no body. */
const NATS_GRANT_GENERATION_PATH = "/internal/nats-projection/grant-generation";
/**
* The path of the retired grant-digests route. core-api serves nothing at it since stage S6 (R-056
* item 2). The constant stays so that a test of the producer can name the path and assert that no
* handler serves it; no client may call it.
*/
const NATS_GRANT_DIGESTS_PATH = "/internal/nats-projection/grant-digests";
/**
* The answer of `GET` {@link NATS_GRANT_GENERATION_PATH}: `{ generation }`.
*
* `generation` is 64 lower-case hex characters. It changes when an input of the grant compile changes
* on any org of the data plane: a grant row write or revoke of one of the four NATS grant types, a membership change of a
* group that holds such a row, or a status change of a stream principal. It changes once more 360 s
* (the grant cache TTL of 300 s plus 60 s) after the last row write or revoke of an org, so a second
* reconcile reads no cache entry from before the write (R-013). Its value
* carries no other meaning: the operator program compares it with the value it read last and with
* nothing else. Strict: a body with any other key fails the parse, and the client treats it as a
* failed read.
*/
const natsGrantGenerationResponseSchema = z.object({ generation: z.string().regex(/^[0-9a-f]{64}$/).describe("The DP-wide grant generation, 64 lower-case hex") }).strict();
//#endregion
export { NATS_GRANT_DIGESTS_PATH, NATS_GRANT_GENERATION_PATH, natsGrantGenerationResponseSchema };

//# sourceMappingURL=nats-grant-generation.js.map